import React, { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Download, CheckCircle, AlertCircle, Calendar } from 'lucide-react';
import axios from 'axios';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FreeAssessmentPage() {
  const container = useRef();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useGSAP(() => {
    gsap.from('.assessment-hero', {
      opacity: 0,
      y: 40,
      duration: 0.8,
    });

    gsap.from('.checklist-preview-item', {
      opacity: 0,
      x: -30,
      duration: 0.6,
      stagger: 0.15,
      scrollTrigger: {
        trigger: '.checklist-preview',
        start: "top 75%",
      }
    });

    gsap.from('.lead-form-card', {
      opacity: 0,
      y: 50,
      duration: 0.8,
      scrollTrigger: {
        trigger: '.lead-form-section',
        start: "top 75%",
      }
    });
  }, { scope: container });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    // Build the payload for Web3Forms
    const submissionData = {
      ...formData,
      access_key: "d0c3d930-76e6-4e03-92fc-bf228949d4dd", // <-- Put your Web3Forms Access Key inside these quotes
      subject: "New Proposal Checklist Request"
    };

    try {
      const response = await axios.post('https://api.web3forms.com/submit', submissionData);
      
      if (response.data.success) {
        setStatus({
          type: 'success',
          message: 'Success! Your Proposal Readiness Checklist request has been received.'
        });
        setIsSubmitting(false);
        setFormData({ name: '', email: '', company: '' });

        // Optional: Open the checklist automatically for them in a new tab!
        window.open('https://yourwebsite.com/your-checklist.pdf', '_blank');
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Lead magnet error:', error);
      setStatus({
        type: 'error',
        message: 'Failed to process request. Please try again or contact us directly.'
      });
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={container} className="page-container">
      {/* Hero Section */}
      <section className="section section-dark">
        <div className="section-content">
          <div className="assessment-hero" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <div className="section-eyebrow">Free Resource</div>
            <h1 style={{ fontSize: '52px', marginBottom: '24px' }}>
              Free Proposal Readiness Checklist
            </h1>
            <p style={{ fontSize: '20px', color: 'var(--text-secondary)', marginBottom: '40px' }}>
              Assess whether your team is ready to respond effectively to RFPs with our comprehensive checklist—developed by former government evaluators and seasoned proposal professionals.
            </p>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="section section-light">
        <div className="section-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }}>
            <div className="checklist-preview">
              <h2 style={{ marginBottom: '32px' }}>What's Inside the Checklist</h2>
              
              <div className="checklist-preview-item" style={{ marginBottom: '24px' }}>
                <CheckCircle size={24} style={{ color: 'var(--gold-primary)', marginRight: '16px', flexShrink: 0 }} />
                <div>
                  <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>Pre-RFP Capture Planning</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>Strategic positioning checklist before the RFP drops</p>
                </div>
              </div>

              <div className="checklist-preview-item" style={{ marginBottom: '24px' }}>
                <CheckCircle size={24} style={{ color: 'var(--gold-primary)', marginRight: '16px', flexShrink: 0 }} />
                <div>
                  <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>Compliance Matrix Template</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>Ensure you address every RFP requirement</p>
                </div>
              </div>

              <div className="checklist-preview-item" style={{ marginBottom: '24px' }}>
                <CheckCircle size={24} style={{ color: 'var(--gold-primary)', marginRight: '16px', flexShrink: 0 }} />
                <div>
                  <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>Win Theme Development</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>Framework for articulating your competitive advantage</p>
                </div>
              </div>

              <div className="checklist-preview-item" style={{ marginBottom: '24px' }}>
                <CheckCircle size={24} style={{ color: 'var(--gold-primary)', marginRight: '16px', flexShrink: 0 }} />
                <div>
                  <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>Proposal Schedule Calculator</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>Timeline planning tool to avoid last-minute chaos</p>
                </div>
              </div>

              <div className="checklist-preview-item">
                <CheckCircle size={24} style={{ color: 'var(--gold-primary)', marginRight: '16px', flexShrink: 0 }} />
                <div>
                  <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>Color Team Review Guidelines</h3>
                  <p style={{ color: 'var(--text-secondary)' }}>Pink, red, and gold team review best practices</p>
                </div>
              </div>
            </div>

            {/* Download Form */}
            <div className="lead-form-section">
              <div className="lead-form-card" style={{
                background: 'var(--bg-overlay)',
                border: '1px solid var(--border-medium)',
                padding: '40px',
              }}>
                <h3 style={{ fontSize: '24px', marginBottom: '24px' }}>Download Your Free Checklist</h3>
                
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label" htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="email">Work Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john.smith@company.com"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label" htmlFor="company">Company Name *</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="form-input"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      placeholder="Your Company"
                    />
                  </div>

                  {status.message && (
                    <div style={{
                      padding: '16px',
                      marginBottom: '20px',
                      background: status.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                      border: `1px solid ${status.type === 'success' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      color: status.type === 'success' ? '#4CAF50' : '#F44336',
                      fontSize: '14px'
                    }}>
                      {status.type === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />}
                      <span>{status.message}</span>
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="form-button"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Processing...' : (
                      <>
                        <Download size={20} style={{ marginRight: '8px' }} />
                        Send Me the Checklist
                      </>
                    )}
                  </button>

                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '16px', textAlign: 'center' }}>
                    We respect your privacy. No spam, ever.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="section section-dark">
        <div className="section-content">
          <div style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <Calendar size={48} style={{ color: 'var(--gold-primary)', marginBottom: '24px' }} />
            <h2 style={{ marginBottom: '24px' }}>Need Expert Guidance?</h2>
            <p style={{ fontSize: '18px', color: 'var(--text-secondary)', marginBottom: '32px' }}>
              Schedule a complimentary 15-minute consultation to discuss your upcoming proposal. We'll help you identify opportunities to improve your win probability and determine if IronQuill is the right partner for your needs.
            </p>
            <a 
              href="https://calendly.com/ironquillconsulting/30min" 
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}
            >
              <Calendar size={20} />
              Schedule Free Consultation
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
